'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  AD_DEFER_IDLE_MEDIAG_MS,
  scheduleDeferredAdLoad,
} from '../../../lib/ad-script-defer';
import {
  adSlotContainerStyle,
  resolveAdSlotSkeletonHeightPx,
} from '../../../lib/ad-slot-dimensions';
import { ensureMgidSiteAndQueueLoadOnce } from '../../../lib/mgid-site-script';

const DEFAULT_INTERSECTION_ROOT_MARGIN = '0px';

export default function MediaGWidget({
  widgetId,
  className = '',
  intersectionRootMargin = DEFAULT_INTERSECTION_ROOT_MARGIN,
  slotHeightPx = 'auto',
  minHeightPx = 'auto',
  maxHeightPx,
  overflowAuto = false,
  visualVariant = 'default',
  postVariantNoBackground = false,
  showLabel = true,
  label = 'Continua depois da publicidade',
}) {
  const isPostVisualVariant = visualVariant === 'post';
  const containerStyle = adSlotContainerStyle({
    slotHeightPx,
    minHeightPx,
    maxHeightPx,
    overflowAuto,
  });
  const skeletonHeightPx = resolveAdSlotSkeletonHeightPx({
    slotHeightPx,
    minHeightPx,
    maxHeightPx,
  });
  const [mounted, setMounted] = useState(false);
  const [inView, setInView] = useState(false);
  const [loadState, setLoadState] = useState('idle');
  const rootRef = useRef(null);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return;
    setInView(false);
    setLoadState('idle');
    const el = rootRef.current;
    if (!el) return;

    let io;
    try {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) setInView(true);
        },
        { root: null, rootMargin: intersectionRootMargin, threshold: 0 }
      );
      io.observe(el);
    } catch {
      setInView(true);
    }

    return () => {
      if (io) io.disconnect();
    };
  }, [mounted, intersectionRootMargin, pathname, widgetId]);

  useEffect(() => {
    if (!mounted || !inView || typeof window === 'undefined') return;

    setLoadState('loading');
    const cancel = scheduleDeferredAdLoad(
      () => {
        ensureMgidSiteAndQueueLoadOnce()
          .then(() => setLoadState('loaded'))
          .catch(() => setLoadState('error'));
      },
      {
        idleTimeout: AD_DEFER_IDLE_MEDIAG_MS,
        waitForInteractionFirst: false,
      }
    );

    return () => {
      cancel();
    };
  }, [widgetId, mounted, inView, isPostVisualVariant, pathname]);

  const boxClass = [
    'adsbgnew',
    'media-g-widget',
    isPostVisualVariant ? 'media-g-widget--post' : '',
    isPostVisualVariant && postVariantNoBackground
      ? 'media-g-widget--no-bg'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const labelBlock =
    showLabel && label && isPostVisualVariant ? (
      <div className="grumft-ad-label grumft-ad-label--post">
        <span>{label}</span>
      </div>
    ) : null;

  if (!mounted) {
    return (
      <div className="article-ad-slot">
        {labelBlock}
        <div
          className={boxClass}
          style={{ ...containerStyle, minHeight: skeletonHeightPx }}
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div className="article-ad-slot" ref={rootRef}>
      {labelBlock}
      <div className="media-g-widget-frame" style={containerStyle}>
        {(loadState === 'idle' || loadState === 'loading') && (
          <div className="grumft-ad-skeleton" aria-hidden />
        )}
        {loadState === 'error' ? (
          <div className="grumft-ad-error" role="status">
            Publicidade indisponível no momento
          </div>
        ) : null}
        <div
          data-type="_mgwidget"
          data-widget-id={String(widgetId)}
          className={boxClass}
        />
      </div>
    </div>
  );
}
